import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { addProjectMember } from "../../../../api/project";
import { RoleMemberProject } from "../../../../constants/user";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "../../../../components/ui/card";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../../../components/ui/select";
import { Avatar, AvatarFallback } from "../../../../components/ui/avatar";
import { Loader2, Search, UserPlus, X, Users } from "lucide-react";
import toastifyInstance from "../../../../utils/toastify";
import { ProjectInterface } from "../../../../models/project.model";
import { UserInterface } from "../../../../models/user.model";
import { useSearchParams } from "react-router-dom";
import { getUsersNotInProject } from "@/api/user";

interface AddMembersTabProps {
    projectId: string;
    project: ProjectInterface;
    onViewMembers: () => void;
}

interface SelectedUser {
    userId: string;
    role: RoleMemberProject;
}

const AddMembersTab: React.FC<AddMembersTabProps> = ({ project, onViewMembers }) => {
    const queryClient = useQueryClient();

    // For adding new members
    const { data: users, isLoading: isLoadingUsers } = useQuery({
        queryKey: ["users-not-in-project", project._id],
        queryFn: () => getUsersNotInProject(project._id),
    });
    const [selectedUsers, setSelectedUsers] = useState<SelectedUser[]>([]);
    const [searchTerm, setSearchTerm] = useState<string>("");

    const [searchParams] = useSearchParams();

    const projectId = searchParams.get("id");

    // Mutation for adding members
    const addMembersMutation = useMutation({
        mutationFn: (payload: {
            projectId: string;
            members: {
                uid: string;
                role: string;
            }[];
        }) => addProjectMember(payload),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["projects"] });
            queryClient.invalidateQueries({ queryKey: ["project-members", projectId] });
            toastifyInstance.success(`${selectedUsers.length} members have been added to the project.`);
            setSelectedUsers([]);
            onViewMembers();
        },
        onError: (error) => {
            console.error(error);
            toastifyInstance.error("Failed to add members to the project.");
        },
    });


    // Handler for adding a user to selection
    const handleAddUser = (userId: string, role: RoleMemberProject) => {
        if (!selectedUsers.some((user) => user.userId === userId)) {
            setSelectedUsers([...selectedUsers, { userId, role }]);
        } else {
            setSelectedUsers(
                selectedUsers.map((user) =>
                    user.userId === userId ? { ...user, role } : user
                )
            );
        }
    };

    // Handler for removing a user from selection
    const handleRemoveUser = (userId: string) => {
        setSelectedUsers(selectedUsers.filter((user) => user.userId !== userId));
    };

    // Submit selected users to add
    const handleSubmitAdd = () => {
        if (selectedUsers.length === 0) {
            toastifyInstance.warning("Please select at least one user to add.");
            return;
        }

        addMembersMutation.mutate({
            projectId: projectId || "",
            members: selectedUsers.map((user) => ({
                uid: user.userId,
                role: user.role,
            })),
        });
    };

    // Helper function to get user by ID
    const getUserById = (userId: string): UserInterface | undefined => {
        return users?.find((user) => user._id === userId);
    };

    // Get initials for avatar
    const getInitials = (name: string): string => {
        return name
            .split(" ")
            .map((n) => n[0])
            .join("")
            .toUpperCase()
            .substring(0, 2);
    };

    return (
        <Card>
            <CardHeader className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-2 sm:space-y-0 pb-2">
                <div className="space-y-1">
                    <CardTitle className="text-lg sm:text-xl font-bold">Add New Team Members</CardTitle>
                    <CardDescription>
                        Add new members to your project team
                    </CardDescription>
                </div>
                <Button
                    onClick={handleSubmitAdd}
                    disabled={addMembersMutation.isPending || selectedUsers.length === 0}
                    className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800 shadow-md hover:shadow-lg transition-all duration-200 font-medium px-4 sm:px-6 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                >
                    {addMembersMutation.isPending ? (
                        <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            <span>Adding...</span>
                        </>
                    ) : (
                        <>
                            <UserPlus className="h-4 w-4" />
                            <span>SAVE</span>
                        </>
                    )}
                </Button>
            </CardHeader>
            <CardContent className="space-y-4 sm:space-y-6">
                <div className="relative">
                    <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                        placeholder="Search users..."
                        className="pl-8 text-sm sm:text-base"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                {selectedUsers.length > 0 && (
                    <Card>
                        <CardHeader className="py-2 sm:py-3">
                            <CardTitle className="text-sm sm:text-base font-medium">
                                Selected Members ({selectedUsers.length})
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="grid gap-3 sm:gap-4">
                                {selectedUsers.map((selected) => {
                                    const user = getUserById(selected.userId);
                                    if (!user) return null;

                                    return (
                                        <Card key={selected.userId} className="p-3 sm:p-4">
                                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-0">
                                                <div className="flex items-center gap-2 sm:gap-3">
                                                    <Avatar className="h-8 w-8 sm:h-10 sm:w-10">
                                                        <AvatarFallback>
                                                            {getInitials(user.name)}
                                                        </AvatarFallback>
                                                    </Avatar>
                                                    <div>
                                                        <p className="font-medium text-sm sm:text-base">{user.name}</p>
                                                        <p className="text-xs sm:text-sm text-muted-foreground">
                                                            {user.email}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2 sm:gap-3">
                                                    <Select
                                                        value={selected.role}
                                                        onValueChange={(value) =>
                                                            handleAddUser(
                                                                selected.userId,
                                                                value as RoleMemberProject
                                                            )
                                                        }
                                                    >
                                                        <SelectTrigger className="w-28 sm:w-32 text-sm">
                                                            <SelectValue placeholder="Select role" />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            {Object.values(RoleMemberProject).map((role) => (
                                                                <SelectItem key={role} value={role}>
                                                                    {role}
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => handleRemoveUser(selected.userId)}
                                                    >
                                                        <X className="h-4 w-4 text-destructive" />
                                                    </Button>
                                                </div>
                                            </div>
                                        </Card>
                                    );
                                })}
                            </div>
                        </CardContent>
                    </Card>
                )}

                <Card>
                    <CardHeader className="py-2 sm:py-3">
                        <CardTitle className="text-sm sm:text-base font-medium">Available Users</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {isLoadingUsers ? (
                            <div className="flex justify-center items-center py-6 sm:py-8">
                                <Loader2 className="h-5 w-5 sm:h-6 sm:w-6 animate-spin text-muted-foreground" />
                            </div>
                        ) : users && users.length > 0 ? (
                            <div className="grid gap-3 sm:gap-4">
                                {users
                                    .filter((user: UserInterface) =>
                                        !selectedUsers.some(selected => selected.userId === user._id) &&
                                        (user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                            user.email.toLowerCase().includes(searchTerm.toLowerCase()))
                                    )
                                    .map((user: UserInterface) => {
                                        return (
                                            <Card
                                                key={user._id}
                                                className="p-3 sm:p-4 transition-colors hover:bg-accent/50"
                                            >
                                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-0">
                                                    <div className="flex items-center gap-2 sm:gap-3">
                                                        <Avatar className="h-8 w-8 sm:h-10 sm:w-10">
                                                            <AvatarFallback>
                                                                {getInitials(user.name)}
                                                            </AvatarFallback>
                                                        </Avatar>
                                                        <div>
                                                            <p className="font-medium text-sm sm:text-base">{user.name}</p>
                                                            <p className="text-xs sm:text-sm text-muted-foreground">
                                                                {user.email}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-2 sm:gap-3">
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={() =>
                                                                handleAddUser(user._id, RoleMemberProject.DEVELOPER)
                                                            }
                                                            className="text-xs sm:text-sm"
                                                        >
                                                            <UserPlus className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                                                            Add
                                                        </Button>
                                                    </div>
                                                </div>
                                            </Card>
                                        );
                                    })}
                            </div>
                        ) : (
                            <div className="flex flex-col items-center justify-center py-6 sm:py-8 text-center">
                                <Users className="h-6 w-6 sm:h-8 sm:w-8 text-muted-foreground mb-2" />
                                <p className="text-xs sm:text-sm text-muted-foreground">No users available</p>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </CardContent>
        </Card>
    );
};

export default AddMembersTab;